import {
  FilterType,
  CustomFilterType,
  NativeFilterTypes,
} from 'design-system-web';
import {
  InvoiceeInfo,
  ExternalPayerType,
} from 'lane-shared/domains/billingPayments/types';
import { useTranslation } from 'react-i18next';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { useMemo } from 'react';
import { searchWorkplaceMembersAndCompanies } from '../../charges-invoices/helpers/searchWorkplaceMembersAndCompanies';

const PER_PAGE = 50;

export const DEFAULT_FILTER_PARAMS = {
  page: 0,
  pageSize: PER_PAGE,
  total: 0,
};

export const useGetTransactionTableFilters = (): (
  | FilterType
  | CustomFilterType<any>
)[] => {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const timeZone = channel?.address?.geo
    ? getTimeZoneByGeoLocation({
        longitude: channel.address.geo[0],
        latitude: channel.address.geo[1],
      })
    : '';

  const customerSearchInput = useMemo(() => {
    if (!channel) {
      return () => [];
    }

    return searchWorkplaceMembersAndCompanies(channel?._id);
  }, [channel]);

  const loadCustomerOptions = async (inputValue: string | string[]) => {
    if (Array.isArray(inputValue)) {
      return [];
    }

    const customers = await customerSearchInput(inputValue);
    const dropdownOptions = customers.map((customer: InvoiceeInfo) => {
      const prefix =
        customer.type === ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY
          ? 'c-'
          : 'u-';

      return {
        label: customer.name,
        value: prefix + customer._id,
      };
    });

    return dropdownOptions;
  };

  const tableFilters: (FilterType | CustomFilterType<any>)[] = [
    {
      key: 'paidBy',
      label: t('abp.transaction.filter.paidBy'),
      type: NativeFilterTypes.Multiselect,
      options: [],
      doTranslation: false,
      isPromoted: true,
      loadOptions: loadCustomerOptions,
      noOptionsMessage: t('abp.transaction.filter.searchOptions'),
    },
    {
      key: 'status',
      label: t('abp.transaction.filter.status'),
      type: NativeFilterTypes.Multiselect,
      options: getFilterableStatuses(),
      isPromoted: true,
    },
    {
      key: 'paymentType',
      label: t('abp.transaction.filter.paymentType'),
      type: NativeFilterTypes.Multiselect,
      options: getFilterablePaymentTypes(),
      isPromoted: true,
    },
    {
      key: 'createdAt',
      label: t('abp.transaction.filter.transactionDate'),
      type: NativeFilterTypes.DateRange,
      isPromoted: true,
      timeZone,
    },
  ];

  return tableFilters;
};

const getFilterableStatuses = (): { label: string; value: string }[] => {
  const statuses = [
    {
      label: 'abp.transaction.status.paid',
      value: 'SUCCEEDED',
    },
    {
      label: 'abp.transaction.status.failed',
      value: 'FAILED',
    },
    {
      label: 'abp.transaction.status.refunded',
      value: 'REFUND_SUCCEEDED',
    },
    {
      label: 'abp.transaction.status.refundFailed',
      value: 'REFUND_FAILED',
    },
  ];

  return statuses;
};

const getFilterablePaymentTypes = (): { label: string; value: string }[] => {
  const statuses = [
    {
      label: 'abp.transaction.paymentMethod.creditCard',
      value: 'PAYMENT_METHOD_CREDIT_CARD',
    },
    {
      label: 'abp.transaction.paymentMethod.credits',
      value: 'PAYMENT_METHOD_CREDITS',
    },
    {
      label: 'abp.transaction.paymentMethod.applePay',
      value: 'PAYMENT_METHOD_APPLE_PAY',
    },
  ];

  return statuses;
};
